import { useEffect, useState, useMemo } from "react";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import BackButton from "../common/BackButton";
import { useNavigate } from "react-router-dom";
import { UserRound, History, X, Download } from "lucide-react";
import * as XLSX from "xlsx";
import ExtensionHistoryModal from "./ExtensionHistoryModal";

const SubscribedUsers = () => {
  const [allSubscribedUsers, setAllSubscribedUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showExtensionHistory, setShowExtensionHistory] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const navigate = useNavigate();

  // Define enum ordering for packageType and planName
  const packageTypeOrder = {
    ONE_BOOK: 1,
    TWO_BOOKS: 2,
    THREE_BOOKS: 3,
  };

  const planNameOrder = {
    ONE_MONTH: 1,
    THREE_MONTHS: 3,
    SIX_MONTHS: 6,
    ONE_YEAR: 12,
  };

  // Fetch the full list once, on mount
  useEffect(() => {
    const fetchSubscribedUsers = async () => {
      setLoading(true);
      try {
        const data = await SubscriptionManager.getAllSubscribedUsersWithDetails();
        setAllSubscribedUsers(data || []);
        setDisplayedUsers(data || []);
      } catch (error) {
        console.error("Failed to fetch subscribed users:", error);
        setAllSubscribedUsers([]);
        setDisplayedUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscribedUsers();
  }, []);

  // Filter displayedUsers when searchTerm changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setDisplayedUsers(allSubscribedUsers);
      return;
    }

    const lower = searchTerm.trim().toLowerCase();
    const filtered = allSubscribedUsers.filter((u) => {
      const nameMatch = u.userName?.toLowerCase().includes(lower);
      const emailMatch = u.emailId?.toLowerCase().includes(lower);
      const planMatch = u.planName?.toLowerCase().includes(lower);
      return nameMatch || emailMatch || planMatch;
    });

    setDisplayedUsers(filtered);
  }, [searchTerm, allSubscribedUsers]);

  // Helper: convert endDate array or string → JS Date
  const parseDateArray = (dateInput) => {
    // Handle ISO string format
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle array format
    if (
      !Array.isArray(dateInput) ||
      dateInput.length < 6 ||
      dateInput.some((n) => typeof n !== "number")
    ) {
      return null;
    }
    const [year, month, day, hour, minute, second] = dateInput;
    return new Date(year, month - 1, day, hour, minute, second);
  };

  // Format date for display
  const formatDateArray = (dateInput) => {
    const d = parseDateArray(dateInput);
    if (!d) return "N/A";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Sorting handler (currently unused but kept for future functionality)
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle direction
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  // Memoized sorted list based on displayedUsers + sortConfig
  const sortedUsers = useMemo(() => {
    const sortable = [...displayedUsers];
    const { key, direction } = sortConfig;
    if (!key) return sortable;

    sortable.sort((a, b) => {
      // Custom sort for planName enum
      if (key === "planName") {
        const aVal = planNameOrder[a.planName] || 0;
        const bVal = planNameOrder[b.planName] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Custom sort for packageType enum
      if (key === "packageType") {
        const aVal = packageTypeOrder[a.packageType] || 0;
        const bVal = packageTypeOrder[b.packageType] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      let aVal = a[key];
      let bVal = b[key];

      // If sorting by endDate or startDate, compare by actual Date objects
      if (key === "endDate" || key === "startDate") {
        const aDate = parseDateArray(aVal);
        const bDate = parseDateArray(bVal);
        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === "asc" ? -1 : 1;
        if (!bDate) return direction === "asc" ? 1 : -1;
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Strings vs numbers
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [displayedUsers, sortConfig]);

  const handleViewUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleViewExtensionHistory = (userId, userName) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowExtensionHistory(true);
  };

  const closeExtensionHistory = () => {
    setShowExtensionHistory(false);
    setSelectedUserId(null);
    setSelectedUserName("");
  };

  // Export to Excel function
  const handleExportToExcel = () => {
    // Prepare data for Excel
    const excelData = sortedUsers.map(user => ({
      "User Name": user.userName || "N/A",
      "Email": user.emailId || "N/A",
      "Mobile Number": user.mobileNumber || "N/A",
      "Plan Name": user.planDisplayName || user.planName?.replace('_', ' ') || "N/A",
      "Package": user.packageName || user.packageType?.replace('_', ' ') || "N/A",
      "Duration": user.planName?.replace('_', ' ').toLowerCase() || "N/A",
      "Remaining Days": user.remainingDays || 0,
      "Is Extended": user.isExtended ? "Yes" : "No",
      "Extension Days": user.extensionDays || 0,
      "Start Date": formatDateArray(user.startDate),
      "End Date": formatDateArray(user.endDate),
      "Status": user.status || "ACTIVE",
      "City": user.cityName || "N/A",
      "Pincode": user.pincode || "N/A",
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 25 }, // User Name
      { wch: 30 }, // Email
      { wch: 15 }, // Mobile Number
      { wch: 20 }, // Plan Name
      { wch: 20 }, // Package
      { wch: 15 }, // Duration
      { wch: 15 }, // Remaining Days
      { wch: 12 }, // Is Extended
      { wch: 15 }, // Extension Days
      { wch: 15 }, // Start Date
      { wch: 15 }, // End Date
      { wch: 12 }, // Status
      { wch: 20 }, // City
      { wch: 12 }, // Pincode
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subscribed Users");

    // Generate filename with current date
    const today = new Date().toISOString().split('T')[0];
    const filename = `Subscribed_Users_${today}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Subscribed Users
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Active subscription management
            </p>
          </div>

          {/* Right side: Search Bar + Download Button */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search by name, email, or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
              />
            </div>

            {/* Download Excel Button */}
            <button
              onClick={handleExportToExcel}
              disabled={sortedUsers.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50"
              title="Download Excel"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download Excel</span>
              <span className="sm:hidden">Excel</span>
            </button>
          </div>
        </div>

        {/* Elite Table Container */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-gray-200/60 dark:border-gray-700/50 overflow-hidden ring-1 ring-black/5 dark:ring-white/5">
            <div className="overflow-x-auto elite-scrollbar">
              <style>{`
                .elite-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .elite-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225);
                  border-radius: 3px;
                  transition: background-color 0.2s ease;
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105);
                }
                .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(148 163 184);
                }
                .dark .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full">
                {/* Elite Header with Enhanced Contrast */}
                <thead className="bg-gray-100/80 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700/70 backdrop-blur-sm">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">User</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Plan</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Start Date</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">End Date</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Duration</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                
                {/* Elite Premium Table Body - Final Elite Polish */}
                <tbody className="bg-white dark:bg-gray-800">
                  {sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        {searchTerm ? `No users match "${searchTerm}."` : "No subscribed users found."}
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user) => (
                      <tr
                        key={user.userId}
                        className="group border-b border-black/[0.04] dark:border-white/[0.04] transition-all duration-200 ease-out hover:bg-black/[0.02] dark:hover:bg-white/[0.04] cursor-pointer"
                      >
                        {/* User Column - Name + Email */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-gray-950 dark:text-white transition-colors duration-150">
                              {user.userName}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {user.emailId}
                            </span>
                          </div>
                        </td>

                        {/* Plan Column - Tightened */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {user.planDisplayName || user.planName?.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {user.packageName || user.packageType?.replace('_', ' ')} · {user.planName?.replace('_', ' ').toLowerCase()} · {user.packageType?.replace('_', ' ').toLowerCase()}
                            </span>
                          </div>
                        </td>

                        {/* Start Date Column */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {formatDateArray(user.startDate)}
                            </span>
                          </div>
                        </td>

                        {/* End Date Column */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {formatDateArray(user.endDate)}
                            </span>
                          </div>
                        </td>

                        {/* Duration Column - Tightened Block */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-150 ${
                                user.remainingDays < 7
                                  ? "text-red-700 dark:text-red-400"
                                  : "text-green-700 dark:text-green-400"
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${
                                  user.remainingDays < 7
                                    ? "bg-red-500 dark:bg-red-400 shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                                    : "bg-green-500 dark:bg-green-400 shadow-[0_0_3px_rgba(34,197,94,0.5)]"
                                }`}></span>
                                {user.remainingDays} days left
                              </span>
                              {user.isExtended && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-50/40 dark:bg-orange-900/15 text-orange-700 dark:text-orange-400 border border-orange-200/30 dark:border-orange-800/25 shadow-sm transition-all duration-150">
                                  +{user.extensionDays}d
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Actions Column - Show on Hover Only */}
                        <td className="px-3 py-2.5 relative">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewUserDetails(user.userId)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                              title="View User Details"
                            >
                              <UserRound className="w-4 h-4" />
                            </button>
                            
                            {/* Extension History Button - Only show if user has extensions */}
                            {user.isExtended && (
                              <button
                                onClick={() => handleViewExtensionHistory(user.userId, user.userName)}
                                className="p-1.5 text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                                title="View Extension History"
                              >
                                <History className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Extension History Modal */}
      <ExtensionHistoryModal
        isOpen={showExtensionHistory}
        onClose={closeExtensionHistory}
        userId={selectedUserId}
        userName={selectedUserName}
      />
    </div>
  );
};

export default SubscribedUsers;
